import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import CouponComponent from '../components/Coupon/Coupon'
import FreeDeliveryComponent from '../components/FreeDelivery/FreeDelivery'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import { getCoupons, deleteCoupon, editCoupon } from '../apollo'
import SearchBar from '../components/TableHeader/SearchBar'
import useGlobalStyles from '../utils/globalStyles'
import { customStyles } from '../utils/tableCustomStyles'
import { RowActionMenu } from '../components/UI/RowActionMenu'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import { Box, Container, Modal, Switch, Button } from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import TableHeader from '../components/TableHeader'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_COUPONS = gql`
  ${getCoupons}
`
const EDIT_COUPON = gql`
  ${editCoupon}
`
const DELETE_COUPON = gql`
  ${deleteCoupon}
`

const Coupon = props => {
  const { t } = props
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [coupon, setCoupon] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const [mutateEdit] = useMutation(EDIT_COUPON)
  const [mutateDelete] = useMutation(DELETE_COUPON, {
    refetchQueries: [{ query: GET_COUPONS }]
  })
  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(
    GET_COUPONS
  )
  const toggleModal = coupon => {
    setEditModal(!editModal)
    setCoupon(coupon)
  }

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field] && isNaN(row[field])) {
        return row[field].toLowerCase()
      }

      return row[field]
    }

    return orderBy(rows, handleField, direction)
  }

  const columns = [
    {
      name: t('Title'),
      sortable: true,
      selector: 'title'
    },
    {
      name: t('Discount'),
      sortable: true,
      selector: 'discount'
    },
    {
      name: t('Discount Type'),
      sortable: true,
      selector: 'discountType',
      cell: row => (
        <>{row.discountType === 'PERCENTAGE' ? 'Percentage' : 'Fixed'}</>
      )
    },
    {
      name: 'Max Coupon Limit',
      sortable: true,
      selector: 'maxCouponLimit',
      cell: row => <>{row.maxCouponLimit ?? '-'}</>
    },
    {
      name: 'Customer Usage Limit',
      sortable: true,
      selector: 'customerUsageLimit',
      cell: row => <>{row.customerUsageLimit ?? '-'}</>
    },
    {
      name: t('Status'),
      cell: row => <>{statusChanged(row)}</>
    },
    {
      name: t('Action'),
      cell: row => <>{actionButtons(row)}</>
    }
  ]
  // Delivery vouchers are listed in their own section below
  const orderCoupons = (data?.coupons || []).filter(
    coupon => coupon.appliesTo !== 'DELIVERY'
  )
  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered =
    searchQuery.length < 3
      ? orderCoupons
      : orderCoupons.filter(coupon => {
          return coupon.title.toLowerCase().search(regex) > -1
        })

  const statusChanged = row => {
    return (
      <>
        {row.enabled}
        <Switch
          size="small"
          defaultChecked={row.enabled}
          onChange={_event => {
            mutateEdit({
              variables: {
                couponInput: {
                  _id: row._id,
                  title: row.title,
                  discount: row.discount,
                  enabled: !row.enabled,
                  discountType: row.discountType,
                  maxCouponLimit: row.maxCouponLimit ?? null,
                  customerUsageLimit: row.customerUsageLimit ?? null
                }
              }
            })
          }}
          style={{ color: 'black' }}
        />
      </>
    )
  }
  const actionButtons = row => {
    return (
      <>
        <div>
          <RowActionMenu
            label={t('Action')}
            actions={[
              {
                label: t('Edit'),
                icon: EditOutlined,
                tone: 'primary',
                onClick: () => {
                  toggleModal(row)
                }
              },
              {
                label: t('Delete'),
                icon: DeleteOutlineRounded,
                tone: 'danger',
                divider: true,
                confirm: {
                  title: `${t('Delete')}?`,
                  message: t('DeleteConfirm'),
                  cancelLabel: t('Cancel')
                },
                onClick: () => {
                  mutateDelete({ variables: { id: row._id } })
                }
              }
            ]}
          />
        </div>
      </>
    )
  }
  const globalClasses = useGlobalStyles()
  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {`${t('Add')} ${t('Coupon')}`}
          </Button>
        }
      />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {errorQuery ? (
          <span>
            `${t('Error')}! ${errorQuery.message}`
          </span>
        ) : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => refetch()}
              />
            }
            title={<TableHeader title={t('Coupons')} />}
            columns={columns}
            data={filtered}
            pagination
            progressPending={loadingQuery}
            progressComponent={<CustomLoader />}
            sortFunction={customSort}
            defaultSortField="title"
            customStyles={customStyles}
          />
        )}
        {/* Delivery-fee vouchers get their own table below the coupons */}
        <FreeDeliveryComponent />
        <Modal
          open={editModal}
          onClose={() => {
            toggleModal(null)
          }}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <CouponComponent
              coupon={coupon}
              onClose={() => toggleModal(null)}
            />
          </Box>
        </Modal>
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <CouponComponent onClose={() => setAddModal(false)} />
          </Box>
        </Modal>
      </Container>
    </>
  )
}

export default withTranslation()(Coupon)
